import { useMemo, useState } from "react";
import { ZodError, type z } from "zod";
import { checkoutBidSchema, checkoutBuyNowSchema, checkoutFormSchema, checkoutHoldSchema, type CheckoutAction, type CheckoutBidInput, type CheckoutBuyNowInput, type CheckoutFormInput, type CheckoutHoldInput } from "../schemas/checkoutFormSchemas";
import { orderService } from "../services/orderService";

export type CheckoutReceipt =
  | { action: "buy"; id: string; status: "payment_pending"; orderUrl: string; message: string }
  | { action: "bid"; id: string; status: "submitted"; message: string }
  | { action: "hold"; id: string; status: "requested"; message: string };

export type CheckoutSubmitResult =
  | { ok: true; receipt: CheckoutReceipt }
  | { ok: false; fieldErrors: Record<string, string>; formError?: string };

function firstFieldErrors(error: ZodError): Record<string, string> {
  const flattened = error.flatten().fieldErrors;
  return Object.fromEntries(
    Object.entries(flattened).flatMap(([field, messages]) => {
      const message = messages?.[0];
      return message ? [[field, message]] : [];
    }),
  );
}

export function submitCheckoutForm(input: CheckoutBuyNowInput): CheckoutSubmitResult;
export function submitCheckoutForm(input: CheckoutBidInput): CheckoutSubmitResult;
export function submitCheckoutForm(input: CheckoutHoldInput): CheckoutSubmitResult;
export function submitCheckoutForm(input: CheckoutFormInput): CheckoutSubmitResult {
  const action = input.action;
  const parsed = checkoutFormSchema.safeParse(input);

  if (!parsed.success) {
    return { ok: false, fieldErrors: firstFieldErrors(parsed.error), formError: "Fix the highlighted fields before submitting." };
  }

  if (action === "buy") {
    const orderInput = checkoutBuyNowSchema.parse(input);
    const order = orderService.createOrder(orderInput);
    return {
      ok: true,
      receipt: {
        action: "buy",
        id: order.id,
        status: order.status as "payment_pending",
        orderUrl: `/orders/${order.id}`,
        message: "Order created and queued for payment review.",
      },
    };
  }

  if (action === "bid") {
    const bidInput = checkoutBidSchema.parse(input);
    const bid = orderService.createBid(bidInput as z.infer<typeof checkoutBidSchema>);
    return {
      ok: true,
      receipt: {
        action: "bid",
        id: bid.id,
        status: bid.status,
        message: "Bid submitted to the seller workflow.",
      },
    };
  }

  const holdInput = checkoutHoldSchema.parse(input);
  const hold = orderService.createHoldRequest(holdInput as z.infer<typeof checkoutHoldSchema>);
  return {
    ok: true,
    receipt: {
      action: "hold",
      id: hold.id,
      status: hold.status,
      message: "Hold request queued for seller approval.",
    },
  };
}

export function useCheckoutForm(defaultAction: CheckoutAction = "buy") {
  const [action, setAction] = useState<CheckoutAction>(defaultAction);
  const [result, setResult] = useState<CheckoutSubmitResult | null>(null);

  return useMemo(() => ({
    action,
    setAction: (nextAction: CheckoutAction) => {
      setAction(nextAction);
      setResult(null);
    },
    result,
    submit: (input: CheckoutFormInput) => {
      const nextResult = submitCheckoutForm(input as never);
      setResult(nextResult);
      return nextResult;
    },
  }), [action, result]);
}
